import React, { useState } from 'react';
import { useMusicPlayer } from '@/contexts/MusicPlayerContext';
import {
  Play, Pause, SkipBack, SkipForward, ChevronUp, ChevronDown,
  Shuffle, Repeat, Repeat1, Volume2, VolumeX, X, ListMusic,
  Music, Heart, ExternalLink, BookOpen, Trash2,
} from 'lucide-react';

const formatTime = (s) => {
  if (!s || isNaN(s)) return '0:00';
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${sec.toString().padStart(2, '0')}`;
};

const MusicPlayerBar = () => {
  const {
    currentTrack, isPlaying, duration, currentTime, volume, shuffle, repeat, expanded, queue, currentIndex,
    togglePlay, next, previous, seek, setVolumeLevel, setShuffle, setRepeat, setExpanded, removeFromQueue, clearQueue,
  } = useMusicPlayer();

  const [showQueue, setShowQueue] = useState(false);

  // Hide player bar when nothing is playing/queued
  if (!currentTrack && queue.length === 0) return null;

  // Hide mini bar when TTS player or recording modal might be active (z-index conflict)
  // The expanded full-screen player handles its own z-index

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;
  const albumArt = currentTrack?.album_art_url || currentTrack?.youtube_url
    ? `https://img.youtube.com/vi/${extractYTId(currentTrack?.youtube_url)}/mqdefault.jpg`
    : null;

  // Mini player (collapsed)
  if (!expanded) {
    return (
      <div className="fixed bottom-0 left-0 right-0 z-40" data-testid="music-player-bar">
        {/* Progress bar (thin line at top of bar) */}
        <div className="h-1 bg-gray-800">
          <div className="h-full bg-gradient-to-r from-indigo-500 to-purple-500 transition-all duration-200"
            style={{ width: `${progress}%` }} />
        </div>
        <div className="bg-gray-900 border-t border-gray-800 px-3 py-2 flex items-center gap-3">
          {/* Album art */}
          <div className="w-10 h-10 rounded-lg overflow-hidden flex-shrink-0 bg-gray-800 flex items-center justify-center cursor-pointer"
            onClick={() => setExpanded(true)}>
            {albumArt ? (
              <img src={albumArt} alt="" className="w-full h-full object-cover" />
            ) : (
              <Music size={18} className="text-gray-500" />
            )}
          </div>

          {/* Track info */}
          <div className="flex-1 min-w-0 cursor-pointer" onClick={() => setExpanded(true)}>
            <p className="text-xs font-bold text-white truncate">{currentTrack?.title || 'No track'}</p>
            <p className="text-[10px] text-gray-400 truncate">{currentTrack?.artist || 'Unknown'}</p>
          </div>

          {/* Controls */}
          <div className="flex items-center gap-1 flex-shrink-0">
            <button onClick={previous} className="p-1.5 text-gray-400 hover:text-white transition-all">
              <SkipBack size={16} />
            </button>
            <button onClick={togglePlay}
              className="w-9 h-9 rounded-full bg-white flex items-center justify-center hover:scale-105 transition-all">
              {isPlaying ? <Pause size={16} className="text-gray-900" /> : <Play size={16} className="text-gray-900 ml-0.5" />}
            </button>
            <button onClick={next} className="p-1.5 text-gray-400 hover:text-white transition-all">
              <SkipForward size={16} />
            </button>
          </div>

          {/* Expand */}
          <button onClick={() => setExpanded(true)} className="p-1 text-gray-500 hover:text-white transition-all">
            <ChevronUp size={18} />
          </button>
        </div>
      </div>
    );
  }

  // Full player (expanded)
  return (
    <div className="fixed inset-0 z-50 bg-gradient-to-b from-gray-900 via-gray-900 to-black flex flex-col"
      data-testid="music-player-full">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3">
        <button onClick={() => setExpanded(false)} className="p-2 text-gray-400 hover:text-white">
          <ChevronDown size={22} />
        </button>
        <p className="text-xs font-bold text-gray-400 uppercase">Now Playing</p>
        <button onClick={() => setShowQueue(!showQueue)} className="p-2 text-gray-400 hover:text-white">
          <ListMusic size={22} />
        </button>
      </div>

      {showQueue ? (
        /* Queue View */
        <div className="flex-1 overflow-y-auto px-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-lg font-black text-white">Queue ({queue.length})</h3>
            <button onClick={clearQueue} className="text-xs font-bold text-gray-500 hover:text-red-400 transition-all">
              Clear All
            </button>
          </div>
          {queue.length === 0 ? (
            <p className="text-center text-gray-500 py-8">Queue is empty</p>
          ) : (
            <div className="space-y-1">
              {queue.map((track, idx) => (
                <div key={`${track.media_id}-${idx}`}
                  className={`flex items-center gap-3 p-2 rounded-lg transition-all ${idx === currentIndex ? 'bg-indigo-500/20' : 'hover:bg-white/5'}`}>
                  <div className="w-8 h-8 rounded bg-gray-800 flex items-center justify-center flex-shrink-0">
                    {idx === currentIndex && isPlaying ? (
                      <div className="flex gap-0.5">
                        <div className="w-0.5 h-3 bg-indigo-400 animate-pulse" />
                        <div className="w-0.5 h-4 bg-indigo-400 animate-pulse" style={{ animationDelay: '0.15s' }} />
                        <div className="w-0.5 h-2 bg-indigo-400 animate-pulse" style={{ animationDelay: '0.3s' }} />
                      </div>
                    ) : (
                      <span className="text-xs text-gray-500 font-bold">{idx + 1}</span>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-bold truncate ${idx === currentIndex ? 'text-indigo-300' : 'text-white'}`}>{track.title}</p>
                    <p className="text-xs text-gray-500 truncate">{track.artist || 'Unknown'}</p>
                  </div>
                  {idx !== currentIndex && (
                    <button onClick={() => removeFromQueue(idx)} className="p-1 text-gray-600 hover:text-red-400">
                      <Trash2 size={14} />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      ) : (
        /* Player View */
        <div className="flex-1 flex flex-col items-center justify-center px-6">
          {/* Album Art */}
          <div className="w-64 h-64 sm:w-72 sm:h-72 rounded-2xl overflow-hidden mb-8 shadow-2xl bg-gray-800 flex items-center justify-center">
            {albumArt ? (
              <img src={albumArt} alt={currentTrack?.title} className="w-full h-full object-cover" />
            ) : (
              <Music size={80} className="text-gray-600" />
            )}
          </div>

          {/* Track Info */}
          <div className="w-full max-w-sm text-center mb-2">
            <h2 className="text-xl font-black text-white truncate">{currentTrack?.title || 'No Track'}</h2>
            <p className="text-sm text-gray-400 mt-1">
              {currentTrack?.artist || 'Unknown Artist'}
              {currentTrack?.artist_url && (
                <a href={currentTrack.artist_url} target="_blank" rel="noopener noreferrer"
                  className="ml-1.5 text-indigo-400 hover:text-indigo-300 inline-flex items-center">
                  <ExternalLink size={12} />
                </a>
              )}
            </p>
            {currentTrack?.stories_appeared_in?.length > 0 && (
              <p className="text-xs text-gray-500 mt-1 flex items-center justify-center gap-1">
                <BookOpen size={10} /> Heard in: {currentTrack.stories_appeared_in[0]}
                {currentTrack.stories_appeared_in.length > 1 && ` +${currentTrack.stories_appeared_in.length - 1} more`}
              </p>
            )}
          </div>

          {/* Progress */}
          <div className="w-full max-w-sm mt-4">
            <input type="range" min="0" max={duration || 1} step="0.1" value={currentTime}
              onChange={(e) => seek(parseFloat(e.target.value))}
              className="w-full h-1 rounded-full appearance-none cursor-pointer accent-indigo-500"
              style={{ background: `linear-gradient(to right, #6366f1 ${progress}%, #374151 ${progress}%)` }} />
            <div className="flex justify-between mt-1">
              <span className="text-[10px] text-gray-500 font-mono">{formatTime(currentTime)}</span>
              <span className="text-[10px] text-gray-500 font-mono">{formatTime(duration)}</span>
            </div>
          </div>

          {/* Controls */}
          <div className="flex items-center gap-5 mt-6">
            <button onClick={setShuffle}
              className={`p-2 transition-all ${shuffle ? 'text-indigo-400' : 'text-gray-500 hover:text-white'}`}>
              <Shuffle size={18} />
            </button>
            <button onClick={previous} className="p-2 text-white hover:scale-110 transition-all">
              <SkipBack size={24} />
            </button>
            <button onClick={togglePlay}
              className="w-14 h-14 rounded-full bg-white flex items-center justify-center hover:scale-105 transition-all shadow-lg">
              {isPlaying ? <Pause size={26} className="text-gray-900" /> : <Play size={26} className="text-gray-900 ml-1" />}
            </button>
            <button onClick={next} className="p-2 text-white hover:scale-110 transition-all">
              <SkipForward size={24} />
            </button>
            <button onClick={setRepeat}
              className={`p-2 transition-all ${repeat !== 'none' ? 'text-indigo-400' : 'text-gray-500 hover:text-white'}`}>
              {repeat === 'one' ? <Repeat1 size={18} /> : <Repeat size={18} />}
            </button>
          </div>

          {/* Volume */}
          <div className="flex items-center gap-2 mt-6 w-full max-w-[200px]">
            <button onClick={() => setVolumeLevel(volume > 0 ? 0 : 0.8)} className="text-gray-500 hover:text-white">
              {volume === 0 ? <VolumeX size={16} /> : <Volume2 size={16} />}
            </button>
            <input type="range" min="0" max="1" step="0.01" value={volume}
              onChange={(e) => setVolumeLevel(parseFloat(e.target.value))}
              className="flex-1 h-1 rounded-full appearance-none cursor-pointer accent-indigo-500"
              style={{ background: `linear-gradient(to right, #6366f1 ${volume * 100}%, #374151 ${volume * 100}%)` }} />
          </div>
        </div>
      )}
    </div>
  );
};

function extractYTId(url) {
  if (!url) return null;
  const m = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/);
  return m ? m[1] : null;
}

export default MusicPlayerBar;
